<template>
  <div class="ag">
    <!-- HERO -->
    <header class="ag-hero">
      <div class="ag-hero__inner">
        <div class="ag-hero__main">
          <div class="ag-eyebrow"><i class="bi bi-calendar-range"></i> Informes · Por año</div>
          <div class="d-flex align-items-center gap-2 flex-wrap mt-2">
            <div class="ag-nav">
              <button type="button" :disabled="!anoPrevDisponible" aria-label="Año anterior" @click="moverAno(-1)">
                <i class="bi bi-chevron-left"></i>
              </button>
              <select v-model="anoSel" aria-label="Elegir año">
                <option v-for="a in anosDesc" :key="a.ano" :value="a.ano">{{ a.ano }}</option>
              </select>
              <button type="button" :disabled="!anoSigDisponible" aria-label="Año siguiente" @click="moverAno(1)">
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
            <span v-if="esAnoEnCurso" class="ag-chip-delta"><i class="bi bi-hourglass-split"></i> {{ progresoAno.toFixed(0) }}% del año</span>
          </div>
          <div class="ag-hero__cifra">{{ euros(totalAno) }}</div>
          <p class="ag-hero__frase" v-html="fraseHero"></p>
          <div v-if="esAnoEnCurso" class="ag-progreso mb-3" :title="`${progresoAno.toFixed(0)}% del año transcurrido`">
            <span :style="{ width: `${progresoAno}%` }"></span>
          </div>
          <div class="d-flex flex-wrap gap-2">
            <span v-if="deltaAnoAnterior != null" class="ag-chip-delta" :class="deltaAnoAnterior > 0 ? 'sube' : 'baja'">
              <i class="bi" :class="deltaAnoAnterior > 0 ? 'bi-arrow-up-right' : 'bi-arrow-down-right'"></i>
              {{ textoVariacion(deltaAnoAnterior) }} vs. {{ Number(anoSel) - 1 }}{{ esAnoEnCurso ? ' a misma fecha' : '' }}
            </span>
            <span v-if="anosDesc.length > 1" class="ag-chip-delta">
              Media anual: {{ euros(mediaAnual, { redondo: true }) }}
            </span>
          </div>
        </div>
        <div class="ag-hero__side">
          <div class="ag-hero__stats">
            <div class="ag-stat">
              <div class="ag-stat__label">Gastos</div>
              <div class="ag-stat__valor">{{ gastosAno.length }}</div>
              <div class="ag-stat__sub">{{ (gastosAno.length / Math.max(1, mesesTranscurridos)).toFixed(0) }} al mes</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Media mensual</div>
              <div class="ag-stat__valor">{{ euros(totalAno / Math.max(1, mesesTranscurridos), { redondo: true }) }}</div>
              <div class="ag-stat__sub">{{ euros(totalAno / Math.max(1, diasTranscurridos)) }} al día</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Mes más caro</div>
              <div class="ag-stat__valor">{{ mesMax ? euros(mesMax.total, { redondo: true }) : '—' }}</div>
              <div class="ag-stat__sub text-capitalize">{{ mesMax ? MESES_LARGOS[mesMax.idx] : '' }}</div>
            </div>
            <div class="ag-stat">
              <div class="ag-stat__label">Mes más barato</div>
              <div class="ag-stat__valor">{{ mesMin ? euros(mesMin.total, { redondo: true }) : '—' }}</div>
              <div class="ag-stat__sub text-capitalize">{{ mesMin ? MESES_LARGOS[mesMin.idx] : '' }}</div>
            </div>
            <template v-if="hayIngresos">
              <div class="ag-stat">
                <div class="ag-stat__label">Ingresos</div>
                <div class="ag-stat__valor">{{ euros(ingresosAno, { redondo: true }) }}</div>
                <div class="ag-stat__sub">del año</div>
              </div>
              <div class="ag-stat">
                <div class="ag-stat__label">Ahorro</div>
                <div class="ag-stat__valor" :style="{ color: ingresosAno - totalAno >= 0 ? '#c8f5d9' : '#ffd3cf' }">
                  {{ euros(ingresosAno - totalAno, { redondo: true }) }}
                </div>
                <div class="ag-stat__sub">{{ ingresosAno ? `tasa ${(((ingresosAno - totalAno) / ingresosAno) * 100).toFixed(0)}%` : 'sin ingresos' }}</div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </header>

    <div class="ag-body">
      <div v-if="!anosDesc.length" class="ag-card ag-vacio">Todavía no hay gastos registrados.</div>
      <template v-else>
        <!-- INSIGHTS -->
        <div v-if="insights.length" class="ag-insights">
          <div v-for="(ins, i) in insights" :key="i" class="ag-insight">
            <div class="ag-insight__icono"><i class="bi" :class="ins.icono"></i></div>
            <div class="ag-insight__texto" v-html="ins.html"></div>
          </div>
        </div>

        <div class="ag-grid">
          <!-- CARRERA DE AÑOS -->
          <section class="ag-card ag-span-8">
            <div class="ag-card__head">
              <div>
                <h2 class="ag-card__titulo">La carrera de los años</h2>
                <div class="ag-card__sub">Gasto acumulado de enero a diciembre</div>
              </div>
              <div class="ag-leyenda">
                <span v-for="s in seriesCarrera" :key="s.ano" :style="{ fontWeight: s.ano === anoSel ? 700 : 400 }">
                  <i :style="{ background: s.color, height: s.ano === anoSel ? '4px' : '2px' }"></i>{{ s.ano }}
                </span>
              </div>
            </div>
            <div class="ag-chart ag-chart--alto">
              <Line :data="carreraData" :options="carreraOptions" />
            </div>
          </section>

          <!-- AÑO CONTRA AÑO -->
          <section class="ag-card ag-span-4">
            <div class="ag-card__head">
              <h2 class="ag-card__titulo">Año contra año</h2>
              <span class="ag-card__sub">{{ anosDesc.length }} años</span>
            </div>
            <ul class="ag-rank">
              <li
                v-for="a in anosDesc"
                :key="a.ano"
                class="ag-rank__item"
                :class="{ activo: a.ano === anoSel }"
                role="button"
                tabindex="0"
                @click="anoSel = a.ano"
                @keydown.enter="anoSel = a.ano"
              >
                <span class="ag-dot" :style="{ background: a.ano === anoSel ? '#1f6b45' : '#c9d2cb' }"></span>
                <span class="ag-rank__nombre">{{ a.ano }}<small v-if="a.ano === anoHoy" class="text-muted fw-normal"> · en curso</small></span>
                <span class="ag-rank__valor">{{ euros(a.total, { redondo: true }) }}</span>
                <span class="ag-rank__barra">
                  <span :style="{ width: `${(a.total / maxAno) * 100}%`, background: a.ano === anoSel ? '#1f6b45' : '#8cc3a0' }"></span>
                </span>
                <span class="ag-rank__meta">
                  <span>{{ a.num }} gastos<template v-if="hayIngresos"> · balance {{ euros((ingresosPorAno[a.ano] || 0) - a.total, { redondo: true }) }}</template></span>
                  <span class="ag-delta" :class="claseDelta(deltaYoY(a))">{{ textoVariacion(deltaYoY(a)) }}</span>
                </span>
              </li>
            </ul>
          </section>
        </div>

        <!-- MATRIZ AÑO × MES -->
        <section class="ag-card mb-3">
          <div class="ag-card__head">
            <div>
              <h2 class="ag-card__titulo">Tu historia en un vistazo</h2>
              <div class="ag-card__sub">Cada celda es un mes: cuanto más oscuro, más gasto. Toca una para abrirla.</div>
            </div>
          </div>
          <div class="ag-matriz__scroll">
            <table class="ag-matriz">
              <thead>
                <tr>
                  <th></th>
                  <th v-for="m in MESES_CORTOS" :key="m">{{ m }}</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="fila in matriz" :key="fila.ano" :class="{ activo: fila.ano === anoSel }">
                  <th scope="row">
                    <button type="button" class="ag-matriz__ano" @click="anoSel = fila.ano">{{ fila.ano }}</button>
                  </th>
                  <td v-for="(c, i) in fila.celdas" :key="i">
                    <button
                      v-if="c.existe"
                      type="button"
                      class="ag-matriz__celda"
                      :class="`n${c.nivel}`"
                      :title="`${MESES_LARGOS[i]} ${fila.ano}: ${euros(c.total)}`"
                      @click="abrirMes(fila.ano, i)"
                    >{{ compacto(c.total) }}</button>
                    <span v-else class="ag-matriz__celda vacia"></span>
                  </td>
                  <td class="text-end ag-cifra">{{ euros(fila.total, { redondo: true }) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <div class="ag-grid">
          <!-- MESES DEL AÑO -->
          <section class="ag-card ag-span-6">
            <div class="ag-card__head">
              <div>
                <h2 class="ag-card__titulo">Mes a mes</h2>
                <div class="ag-leyenda mt-1">
                  <span><i style="background:#1f6b45;height:0.7rem;width:0.7rem;border-radius:3px"></i>{{ anoSel }}</span>
                  <span><i style="background:#b8c4bc;height:0.7rem;width:0.7rem;border-radius:3px"></i>{{ Number(anoSel) - 1 }}</span>
                </div>
              </div>
              <span class="ag-card__sub">Toca un mes para filtrar</span>
            </div>
            <div class="ag-chart">
              <Bar :data="mesesData" :options="mesesOptions" />
            </div>
          </section>

          <!-- CATEGORÍAS DEL AÑO -->
          <section class="ag-card ag-span-6">
            <div class="ag-card__head">
              <h2 class="ag-card__titulo">Categorías de {{ anoSel }}</h2>
              <span class="ag-card__sub">vs. {{ Number(anoSel) - 1 }}</span>
            </div>
            <div v-if="!rankingAno.length" class="ag-vacio">Sin gastos</div>
            <template v-else>
              <div class="ag-tira">
                <span
                  v-for="c in rankingAno"
                  :key="c.categoria"
                  :style="{ flexGrow: c.total, background: colorCat(c.categoria), opacity: catSel && catSel !== c.categoria ? 0.3 : 1 }"
                  :title="`${c.categoria}: ${euros(c.total)}`"
                  @click="toggleCat(c.categoria)"
                ></span>
              </div>
              <ul class="ag-rank mt-2">
                <li
                  v-for="c in rankingAno.slice(0, 8)"
                  :key="c.categoria"
                  class="ag-rank__item"
                  :class="{ activo: catSel === c.categoria, atenuado: catSel && catSel !== c.categoria }"
                  role="button"
                  tabindex="0"
                  @click="toggleCat(c.categoria)"
                  @keydown.enter="toggleCat(c.categoria)"
                >
                  <span class="ag-dot" :style="{ background: colorCat(c.categoria) }"></span>
                  <span class="ag-rank__nombre">{{ c.categoria }}</span>
                  <span class="ag-rank__valor">{{ euros(c.total, { redondo: true }) }}</span>
                  <span class="ag-rank__barra"><span :style="{ width: `${(c.total / rankingAno[0].total) * 100}%`, background: colorCat(c.categoria) }"></span></span>
                  <span class="ag-rank__meta">
                    <span>{{ c.pct.toFixed(1) }}% · {{ euros(c.total / Math.max(1, mesesTranscurridos), { redondo: true }) }}/mes</span>
                    <span class="ag-delta" :class="claseDelta(deltaCatAno(c))">{{ catAnoAnterior[c.categoria] ? textoVariacion(deltaCatAno(c)) : 'nueva' }}</span>
                  </span>
                </li>
              </ul>
            </template>
          </section>
        </div>

        <div class="ag-grid">
          <!-- MAPA DIARIO -->
          <section class="ag-card ag-span-8">
            <div class="ag-card__head">
              <h2 class="ag-card__titulo">{{ anoSel }}, día a día</h2>
              <span class="ag-card__sub">Pasa por encima para ver el importe</span>
            </div>
            <AgMapaAno :ano="anoSel" :totales-dia="totalesDiaAno" />
          </section>

          <!-- PODIO -->
          <section class="ag-card ag-span-4">
            <div class="ag-card__head">
              <h2 class="ag-card__titulo">Podio del año</h2>
            </div>
            <ol class="ag-podio">
              <li v-for="g in podio" :key="g.id">
                <div style="min-width:0">
                  <div class="ag-podio__concepto">{{ g.concepto || 'Sin concepto' }}</div>
                  <div class="ag-podio__meta">
                    <span class="ag-dot" :style="{ background: colorCat(categoriaDe(g)) }"></span>
                    {{ fechaCorta(diaISO(g)) }}
                  </div>
                </div>
                <div class="ag-cifra">{{ euros(importe(g), { redondo: true }) }}</div>
              </li>
            </ol>
          </section>
        </div>

        <!-- LISTA -->
        <AgListaGastos :gastos="gastosLista" :colores="colores" :titulo="`Gastos de ${anoSel}`">
          <template #filtros>
            <span v-if="mesFiltro != null" class="ag-filtro-activo text-capitalize">
              {{ MESES_LARGOS[mesFiltro] }}
              <button type="button" aria-label="Quitar filtro de mes" @click="mesFiltro = null"><i class="bi bi-x"></i></button>
            </span>
            <span v-if="catSel" class="ag-filtro-activo">
              {{ catSel }}
              <button type="button" aria-label="Quitar filtro de categoría" @click="catSel = null"><i class="bi bi-x"></i></button>
            </span>
          </template>
        </AgListaGastos>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from '../config/axios';
import { useStore } from 'vuex';
import { Bar, Line } from 'vue-chartjs';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Filler, Legend
} from 'chart.js';
import {
  importe, diaISO, categoriaDe, euros, sumar, variacion, textoVariacion, agruparPor, claveAno,
  mapaColoresCategorias, rankingCategorias, acumuladoMensualAno, totalesPorDia, nivelCalorRango,
  aISO, parseISO, diasEntre, MESES_CORTOS, MESES_LARGOS, COLOR_OTRAS
} from '../utils/analisisGastos';
import AgListaGastos from '../components/analisis/AgListaGastos.vue';
import AgMapaAno from '../components/analisis/AgMapaAno.vue';
import '../assets/analisis-gastos.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Filler, Legend);

const escapar = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export default {
  name: 'GastosPorAnoView',
  components: { Bar, Line, AgListaGastos, AgMapaAno },
  setup() {
    const store = useStore();
    const router = useRouter();
    const hasIngresos = computed(() => store.getters['auth/hasIngresos']);
    const ingresosRaw = ref([]);

    const todos = computed(() => store.getters.getGastos || []);
    const colores = computed(() => mapaColoresCategorias(todos.value));
    const colorCat = (c) => colores.value[c] || COLOR_OTRAS;

    const hoy = new Date();
    const hoyISO = aISO(hoy);
    const anoHoy = hoyISO.slice(0, 4);

    // Años desde el primero con gastos hasta el actual (con ceros incluidos).
    const porAno = computed(() => agruparPor(todos.value, claveAno));
    const anosAsc = computed(() => {
      const claves = [...porAno.value.keys()].filter((a) => a <= anoHoy).sort();
      if (!claves.length) return [];
      const out = [];
      for (let a = Number(claves[0]); a <= Number(anoHoy); a++) {
        const x = porAno.value.get(String(a));
        out.push({ ano: String(a), total: x?.total || 0, num: x?.num || 0 });
      }
      return out;
    });
    const anosDesc = computed(() => [...anosAsc.value].reverse());
    const maxAno = computed(() => Math.max(...anosAsc.value.map((a) => a.total), 1));

    const ingresosPorAno = computed(() => {
      const m = {};
      ingresosRaw.value.forEach((i) => { m[String(i.ano)] = i.total; });
      return m;
    });
    const hayIngresos = computed(() => hasIngresos.value && ingresosRaw.value.length > 0);

    // ---------- Selección ----------
    const anoSel = ref(anoHoy);
    const mesFiltro = ref(null);
    const catSel = ref(null);
    watch(anoSel, () => { mesFiltro.value = null; catSel.value = null; });
    watch(anosDesc, (lista) => {
      if (lista.length && !lista.some((a) => a.ano === anoSel.value)) anoSel.value = lista[0].ano;
    });
    const idxSel = computed(() => anosAsc.value.findIndex((a) => a.ano === anoSel.value));
    const anoPrevDisponible = computed(() => idxSel.value > 0);
    const anoSigDisponible = computed(() => idxSel.value >= 0 && idxSel.value < anosAsc.value.length - 1);
    const moverAno = (d) => { const a = anosAsc.value[idxSel.value + d]; if (a) anoSel.value = a.ano; };
    const toggleCat = (c) => { catSel.value = catSel.value === c ? null : c; };

    const esAnoEnCurso = computed(() => anoSel.value === anoHoy);
    const gastosAno = computed(() => todos.value.filter((g) => diaISO(g).startsWith(anoSel.value)));
    const totalAno = computed(() => sumar(gastosAno.value));
    const ingresosAno = computed(() => ingresosPorAno.value[anoSel.value] || 0);
    const diasAno = computed(() => diasEntre(`${anoSel.value}-01-01`, `${anoSel.value}-12-31`));
    const diasTranscurridos = computed(() => (esAnoEnCurso.value ? diasEntre(`${anoHoy}-01-01`, hoyISO) : diasAno.value));
    const mesesTranscurridos = computed(() => (esAnoEnCurso.value ? hoy.getMonth() + 1 : 12));
    const progresoAno = computed(() => (diasTranscurridos.value / diasAno.value) * 100);
    const acumAno = computed(() => acumuladoMensualAno(todos.value, anoSel.value));

    const mesesConDatos = computed(() =>
      acumAno.value.porMes
        .map((total, idx) => ({ total, idx }))
        .filter((m) => m.idx < mesesTranscurridos.value && (!esAnoEnCurso.value || m.idx < hoy.getMonth()))
    );
    const mesMax = computed(() => (mesesConDatos.value.length ? mesesConDatos.value.reduce((m, x) => (x.total > m.total ? x : m)) : null));
    const mesMin = computed(() => (mesesConDatos.value.length ? mesesConDatos.value.reduce((m, x) => (x.total < m.total ? x : m)) : null));

    // Comparativa con el año anterior: a misma fecha si el año está en curso.
    const totalAnoHasta = (ano, mmdd) => sumar(todos.value.filter((g) => {
      const d = diaISO(g);
      return d.startsWith(ano) && d.slice(5) <= mmdd;
    }));
    const deltaAnoAnterior = computed(() => {
      const prev = String(Number(anoSel.value) - 1);
      if (!porAno.value.has(prev)) return null;
      const base = esAnoEnCurso.value ? totalAnoHasta(prev, hoyISO.slice(5)) : porAno.value.get(prev).total;
      return variacion(totalAno.value, base);
    });
    const deltaYoY = (a) => {
      const prev = porAno.value.get(String(Number(a.ano) - 1));
      if (!prev) return null;
      if (a.ano === anoHoy) return variacion(a.total, totalAnoHasta(String(Number(a.ano) - 1), hoyISO.slice(5)));
      return variacion(a.total, prev.total);
    };
    const claseDelta = (v) => (v == null ? 'neutro' : v > 0.5 ? 'sube' : v < -0.5 ? 'baja' : 'neutro');
    const mediaAnual = computed(() => {
      const cerrados = anosAsc.value.filter((a) => a.ano !== anoHoy);
      return cerrados.length ? cerrados.reduce((s, a) => s + a.total, 0) / cerrados.length : totalAno.value;
    });
    const proyeccion = computed(() => (totalAno.value / Math.max(1, diasTranscurridos.value)) * diasAno.value);

    const fraseHero = computed(() => {
      if (!gastosAno.value.length) return 'Sin gastos registrados en este año.';
      if (esAnoEnCurso.value) {
        return `Quedan <strong>${diasAno.value - diasTranscurridos.value} días</strong>. A este ritmo cerrarás ${anoSel.value} en <strong>~${euros(proyeccion.value, { redondo: true })}</strong>.`;
      }
      const v = variacion(totalAno.value, mediaAnual.value);
      const base = `Unos <strong>${euros(totalAno.value / 12, { redondo: true })} al mes</strong>`;
      if (v == null || anosAsc.value.length < 3) return `${base} durante todo el año.`;
      return `${base}, un ${Math.abs(v).toFixed(0)}% ${v > 0 ? 'por encima' : 'por debajo'} de tu año medio.`;
    });

    // ---------- Categorías ----------
    const rankingAno = computed(() => rankingCategorias(gastosAno.value));
    const catAnoAnterior = computed(() => {
      const prev = String(Number(anoSel.value) - 1);
      const m = {};
      rankingCategorias(todos.value.filter((g) => diaISO(g).startsWith(prev))).forEach((c) => { m[c.categoria] = c.total; });
      return m;
    });
    const deltaCatAno = (c) => variacion(c.total, catAnoAnterior.value[c.categoria]);

    // ---------- Insights ----------
    const totalesDiaAno = computed(() => totalesPorDia(gastosAno.value));
    const insights = computed(() => {
      const out = [];
      if (!gastosAno.value.length) return out;
      const diasCon = Object.keys(totalesDiaAno.value).length;
      const sin = diasTranscurridos.value - diasCon;
      out.push({
        icono: 'bi-piggy-bank',
        html: `<strong>${sin} días</strong> sin gastar ni un euro (${((sin / diasTranscurridos.value) * 100).toFixed(0)}% del año).`
      });
      if (rankingAno.value.length) {
        const top = rankingAno.value[0];
        out.push({
          icono: 'bi-trophy',
          html: `<strong>${escapar(top.categoria)}</strong> se lleva ${euros(top.total, { redondo: true })}: <strong>${top.pct.toFixed(0)}%</strong> de todo el año.`
        });
      }
      const prev = catAnoAnterior.value;
      if (Object.keys(prev).length) {
        const sube = rankingAno.value
          .map((c) => ({ categoria: c.categoria, dif: c.total - (prev[c.categoria] || 0) }))
          .reduce((m, c) => (c.dif > (m?.dif ?? 0) ? c : m), null);
        if (sube && !esAnoEnCurso.value) {
          out.push({ icono: 'bi-graph-up-arrow', html: `<strong>${escapar(sube.categoria)}</strong> es lo que más creció: <strong>+${euros(sube.dif, { redondo: true })}</strong> vs. ${Number(anoSel.value) - 1}.` });
        }
      }
      const dias = Object.entries(totalesDiaAno.value).sort((a, b) => b[1] - a[1]);
      if (dias.length) {
        out.push({ icono: 'bi-fire', html: `Tu día más caro: <strong>${fechaCorta(dias[0][0])}</strong>, con ${euros(dias[0][1], { redondo: true })}.` });
      }
      return out.slice(0, 4);
    });

    // ---------- Carrera de años ----------
    const seriesCarrera = computed(() => {
      const sel = Number(anoSel.value);
      const anos = anosAsc.value.map((a) => a.ano).filter((a) => Number(a) <= sel).slice(-5).reverse();
      return anos.map((ano, i) => ({
        ano,
        color: ano === anoSel.value ? '#1f6b45' : i === 1 ? '#2a78d6' : ['#eb6834', '#6250d6', '#b8c4bc'][Math.min(i - 2, 2)]
      }));
    });
    const carreraData = computed(() => ({
      labels: MESES_CORTOS,
      datasets: seriesCarrera.value.map((s) => {
        const { acumulado } = acumuladoMensualAno(todos.value, s.ano);
        const limite = s.ano === anoHoy ? hoy.getMonth() + 1 : 12;
        const esSel = s.ano === anoSel.value;
        return {
          label: s.ano,
          data: acumulado.map((v, i) => (i < limite ? v : null)),
          borderColor: s.color,
          backgroundColor: esSel ? 'rgba(31,107,69,0.10)' : 'transparent',
          fill: esSel ? 'origin' : false,
          borderWidth: esSel ? 3.5 : 2,
          tension: 0.3,
          pointRadius: esSel ? 3 : 0,
          pointBackgroundColor: s.color,
          pointHoverRadius: 6,
          order: esSel ? 0 : 1
        };
      })
    }));
    const carreraOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 900, easing: 'easeOutQuart' },
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          itemSort: (a, b) => (b.raw ?? 0) - (a.raw ?? 0),
          callbacks: {
            title: (items) => `Hasta ${MESES_LARGOS[items[0].dataIndex]}`,
            label: (ctx) => (ctx.raw == null ? null : ` ${ctx.dataset.label}: ${euros(ctx.raw, { redondo: true })}`)
          }
        }
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: '#5c675e' } },
        y: {
          beginAtZero: true,
          border: { display: false },
          grid: { color: 'rgba(22, 53, 40, 0.06)' },
          ticks: { color: '#5c675e', callback: (v) => euros(v, { redondo: true }) }
        }
      }
    };

    // ---------- Matriz año × mes ----------
    const matriz = computed(() => {
      const filas = anosDesc.value.map((a) => {
        const { porMes } = acumuladoMensualAno(todos.value, a.ano);
        return { ano: a.ano, total: a.total, porMes };
      });
      const mesHoy = hoyISO.slice(0, 7);
      const primerMes = [...todos.value.map(diaISO).filter(Boolean)].sort()[0]?.slice(0, 7) || mesHoy;
      // El mes en curso (incompleto) no entra en la escala para no deformarla.
      const valores = filas.flatMap((f) => f.porMes.filter((v, i) => v > 0 && `${f.ano}-${String(i + 1).padStart(2, '0')}` !== mesHoy));
      const max = Math.max(...valores, 0);
      const min = Math.min(...valores, max);
      return filas.map((f) => ({
        ...f,
        celdas: f.porMes.map((total, i) => {
          const mes = `${f.ano}-${String(i + 1).padStart(2, '0')}`;
          return { total, nivel: nivelCalorRango(Math.min(total, max), min, max), existe: mes >= primerMes && mes <= mesHoy };
        })
      }));
    });
    const compacto = (v) => {
      if (!v) return '0';
      if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1).replace('.', ',')}k`;
      return Math.round(v);
    };
    const abrirMes = (ano, idx) => {
      router.push({ name: 'GastosPorMes', query: { mes: `${ano}-${String(idx + 1).padStart(2, '0')}` } });
    };

    // ---------- Mes a mes ----------
    const mesesData = computed(() => {
      const actual = acumAno.value.porMes;
      const prev = acumuladoMensualAno(todos.value, String(Number(anoSel.value) - 1)).porMes;
      return {
        labels: MESES_CORTOS,
        datasets: [
          {
            label: String(Number(anoSel.value) - 1),
            data: prev,
            backgroundColor: '#b8c4bc',
            borderRadius: 4,
            borderSkipped: 'bottom',
            maxBarThickness: 18
          },
          {
            label: anoSel.value,
            data: actual.map((v, i) => (esAnoEnCurso.value && i > hoy.getMonth() ? null : v)),
            backgroundColor: actual.map((_, i) => (mesFiltro.value == null || mesFiltro.value === i ? '#1f6b45' : 'rgba(31,107,69,0.3)')),
            borderRadius: 4,
            borderSkipped: 'bottom',
            maxBarThickness: 18
          }
        ]
      };
    });
    const mesesOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 600 },
      interaction: { mode: 'index', intersect: false },
      onClick: (_e, els) => {
        if (!els.length) return;
        const i = els[0].index;
        mesFiltro.value = mesFiltro.value === i ? null : i;
      },
      onHover: (e, els) => { if (e.native?.target) e.native.target.style.cursor = els.length ? 'pointer' : 'default'; },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => MESES_LARGOS[items[0].dataIndex],
            label: (ctx) => (ctx.raw == null ? null : ` ${ctx.dataset.label}: ${euros(ctx.raw)}`),
            afterBody: (items) => {
              const a = items.find((x) => x.dataset.label === anoSel.value)?.raw;
              const b = items.find((x) => x.dataset.label !== anoSel.value)?.raw;
              const v = a != null ? variacion(a, b) : null;
              return v != null ? [`${textoVariacion(v)} interanual`] : [];
            }
          }
        }
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: '#5c675e' } },
        y: {
          beginAtZero: true,
          border: { display: false },
          grid: { color: 'rgba(22, 53, 40, 0.06)' },
          ticks: { color: '#5c675e', callback: (v) => euros(v, { redondo: true }) }
        }
      }
    }));

    // ---------- Podio / lista ----------
    const podio = computed(() => [...gastosAno.value].sort((a, b) => importe(b) - importe(a)).slice(0, 5));
    const gastosLista = computed(() => gastosAno.value.filter((g) =>
      (mesFiltro.value == null || parseInt(diaISO(g).slice(5, 7), 10) - 1 === mesFiltro.value) &&
      (!catSel.value || categoriaDe(g) === catSel.value)
    ));

    const fechaCorta = (iso) => parseISO(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });

    onMounted(async () => {
      const tareas = [
        axios.get('/api/gastos/todos').then(({ data }) => store.commit('setGastos', data)).catch((e) => console.error('Error al cargar los gastos:', e))
      ];
      if (hasIngresos.value) {
        tareas.push(axios.get('/api/ingresos/stats/por-ano').then(({ data }) => { ingresosRaw.value = data; }).catch(() => { ingresosRaw.value = []; }));
      }
      await Promise.all(tareas);
    });

    return {
      hasIngresos, hayIngresos, ingresosPorAno, colores, colorCat, anoHoy,
      anosDesc, maxAno, anoSel, mesFiltro, catSel, moverAno, anoPrevDisponible, anoSigDisponible, toggleCat,
      esAnoEnCurso, gastosAno, totalAno, ingresosAno, diasTranscurridos, mesesTranscurridos, progresoAno,
      mesMax, mesMin, deltaAnoAnterior, deltaYoY, claseDelta, mediaAnual, fraseHero,
      rankingAno, catAnoAnterior, deltaCatAno, totalesDiaAno, insights,
      seriesCarrera, carreraData, carreraOptions,
      matriz, compacto, abrirMes, mesesData, mesesOptions,
      podio, gastosLista,
      euros, importe, diaISO, categoriaDe, textoVariacion, fechaCorta, MESES_CORTOS, MESES_LARGOS
    };
  }
};
</script>

<style scoped>
.ag-progreso { height: 6px; border-radius: 999px; background: rgba(255, 255, 255, 0.12); overflow: hidden; max-width: 28rem; }
.ag-progreso span { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #8cc3a0, #f6d77b); }
.ag-matriz__scroll { overflow-x: auto; }
.ag-matriz { width: 100%; border-collapse: separate; border-spacing: 4px; min-width: 640px; }
.ag-matriz th { font-size: 0.72rem; color: var(--tinta-suave); font-weight: 600; text-transform: capitalize; text-align: center; }
.ag-matriz th[scope="row"] { text-align: left; }
.ag-matriz td.text-end { font-size: 0.85rem; padding-left: 0.5rem; white-space: nowrap; }
.ag-matriz__ano { border: 0; background: transparent; font-weight: 700; color: var(--tinta); padding: 0.2rem 0.4rem; border-radius: 0.4rem; }
.ag-matriz tr.activo .ag-matriz__ano { background: var(--marca-oscura); color: #fff; }
.ag-matriz__celda {
  width: 100%;
  height: 2.2rem;
  border: 0;
  border-radius: 0.45rem;
  display: grid;
  place-items: center;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
  background: #eef1ec;
  color: var(--tinta);
  transition: transform 0.12s;
}
button.ag-matriz__celda:hover { transform: scale(1.08); box-shadow: 0 3px 8px rgba(22, 53, 40, 0.18); }
.ag-matriz__celda.vacia { background: transparent; border: 1px dashed var(--linea); }
.ag-matriz__celda.n1 { background: #c9e3d2; }
.ag-matriz__celda.n2 { background: #8cc3a0; }
.ag-matriz__celda.n3 { background: #3f8f63; color: #fff; }
.ag-matriz__celda.n4 { background: #1a5a3a; color: #fff; }
.ag-matriz tr.activo td .ag-matriz__celda:not(.vacia) { box-shadow: inset 0 0 0 2px rgba(235, 104, 52, 0.7); }
</style>
